import { useEffect } from 'react';
import { answer, callsNow, confKey, decline, hangup, holdOrResume, leaveCall, leftCall, onCallsState, peopleOn, toggleMute as toggleCallMute } from '../calls';
import { leavePatch, onPatchState, patchBusy, patchNow, toggleMute as togglePatchMute } from '../patch';
import { declineRing, joinRing, onPatchRings, patchRingsNow } from '../patchRing';
import { leaveMeeting, meetingNow, onMeetingState, toggleMeetingMute } from '../meeting';
import { registerHeadsetSurface, startHeadset } from './index';

/**
 * Where a headset's buttons go (1.13.0, Teampace 3141), in this order: a call in the app (the one
 * this device has the sound for, or one ringing in), then a Patch (one this device is in, or one
 * ringing), then a meeting. Each does what its own buttons do: Hang up on a call with several people
 * leaves it (the others carry on), as the card's Leave does; it never ends it for everyone.
 */

const ringingCall = () => {
  const s = callsNow();
  return s.on ? s.calls.find((c) => c.direction === 'inbound' && c.state === 'ringing') : undefined;
};
const myCall = () => {
  const s = callsNow();
  const c = s.mine ? s.calls.find((x) => x.id === s.mine) : undefined;
  return c && (c.state === 'active' || c.state === 'dialing') && !leftCall(c) ? c : undefined;
};
const ringingPatch = () => patchRingsNow().find((r) => !r.quiet);
const meetingOn = () => ['joining', 'connected', 'reconnecting'].includes(meetingNow().phase);

let started = false;

export function startHeadsetSurfaces(): void {
  if (started) return;
  started = true;
  registerHeadsetSurface('calls', {
    order: 0,
    ringing: () => Boolean(ringingCall()),
    active: () => Boolean(myCall()),
    muted: () => callsNow().muted,
    held: () => Boolean(myCall()?.held),
    answer: () => {
      const c = ringingCall();
      return c ? answer(c.id) : undefined;
    },
    decline: () => {
      const c = ringingCall();
      return c ? decline(c.id) : undefined;
    },
    hangup: () => {
      const c = myCall();
      if (!c) return undefined;
      return peopleOn(confKey(c)).length > 1 ? leaveCall(c.id) : hangup(c.id);
    },
    toggleMute: () => toggleCallMute(),
    hold: () => {
      const c = myCall();
      return c && c.state === 'active' ? holdOrResume(c.id, !c.held) : undefined;
    },
    watch: onCallsState
  });
  registerHeadsetSurface('patch', {
    order: 1,
    ringing: () => Boolean(ringingPatch()),
    active: () => patchBusy(),
    muted: () => patchNow().muted,
    answer: () => {
      const r = ringingPatch();
      return r ? joinRing(r) : undefined;
    },
    decline: () => {
      const r = ringingPatch();
      return r ? declineRing(r) : undefined;
    },
    hangup: () => leavePatch(),
    toggleMute: () => togglePatchMute(),
    watch: (changed) => {
      const a = onPatchState(changed);
      const b = onPatchRings(changed);
      return () => {
        a();
        b();
      };
    }
  });
  registerHeadsetSurface('meeting', {
    order: 2,
    ringing: () => false,
    active: meetingOn,
    muted: () => meetingNow().muted,
    hangup: () => leaveMeeting(),
    toggleMute: () => toggleMeetingMute(),
    watch: onMeetingState
  });
  startHeadset();
}

/** In the app's shell: the headset, once Stitch is open for a company. */
export function useHeadsetSurfaces(on: boolean): void {
  useEffect(() => {
    if (on) startHeadsetSurfaces();
  }, [on]);
}
